import React, { useState, useRef, useEffect } from 'react';
import { HiMicrophone, HiCheck, HiRefresh, HiVolumeUp } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { setAudioSwitchInProgress, setSelectedAudioDevice, setAudioOutputDevices, setSelectedMicDevice, setMicInputDevices } from 'store/reducers/livestreamSettings';
import { FiSpeaker } from "react-icons/fi";
import { IoHeadset } from "react-icons/io5";
interface Props {
  showTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const AudioDeviceSelector: React.FC<Props> = ({ showTrigger = true, open, onOpenChange }) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();

  const {
    audioOutputDevices = [],
    selectedAudioDevice = '',
    micInputDevices = [],
    selectedMicDevice = '',
    audioSwitchInProgress = false
  } = useSelector((state: any) => state.live.settings || {});

  const [isOpen, setIsOpen] = useState(open || false);
  const [activeTab, setActiveTab] = useState<'output' | 'input'>('output');

  const isControlled = open !== undefined && onOpenChange !== undefined;

  useEffect(() => {
    console.log('AudioDeviceSelector: Component mounted');
    handleRefreshDevices();
    
    const handleDeviceChange = () => {
      console.log('AudioDeviceSelector: Device change detected');
      handleRefreshDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  useEffect(() => {
    console.log('AudioDeviceSelector: Redux state updated', {
      audioOutputDevices: audioOutputDevices.length,
      selectedAudioDevice,
      micInputDevices: micInputDevices.length,
      selectedMicDevice
    });
  }, [audioOutputDevices, selectedAudioDevice, micInputDevices, selectedMicDevice]);

  useEffect(() => {
    if (isControlled) {
      setIsOpen(open || false);
    }
  }, [open, isControlled]);

  const handleToggle = () => {
    const newOpen = !isOpen;
    console.log('AudioDeviceSelector: Toggle clicked, new open state:', newOpen);
    setIsOpen(newOpen);
    if (isControlled) {
      onOpenChange!(newOpen);
    }
    
    if (newOpen) {
      handleRefreshDevices();
    }
  };

  const handleClose = () => {
    console.log('AudioDeviceSelector: Closing dropdown');
    setIsOpen(false);
    if (isControlled) {
      onOpenChange!(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleAudioOutputChange = async (deviceId: string) => {
    if (audioSwitchInProgress || deviceId === selectedAudioDevice) {
      console.log('AudioDeviceSelector: Output change skipped - same device or in progress');
      return;
    }
    
    try {
      console.log('AudioDeviceSelector: Starting output device change to:', deviceId);
      dispatch(setAudioSwitchInProgress(true));
      
      // Find device info for logging
      const device = audioOutputDevices.find(d => d.deviceId === deviceId);
      console.log('AudioDeviceSelector: Changing to device:', device?.label || deviceId);
      
      dispatch(setSelectedAudioDevice(deviceId));
      console.log('AudioDeviceSelector: Redux state updated with new device');
      
    } catch (error) {
      console.error('AudioDeviceSelector: Failed to change audio output device:', error);
    } finally {
      dispatch(setAudioSwitchInProgress(false));
      console.log('AudioDeviceSelector: Output device change completed');
    }
  };

  const handleMicInputChange = async (deviceId: string) => {
    if (audioSwitchInProgress || deviceId === selectedMicDevice) {
      console.log('AudioDeviceSelector: Mic change skipped - same device or in progress');
      return;
    }
    
    try {
      console.log('AudioDeviceSelector: Starting microphone change to:', deviceId);
      dispatch(setAudioSwitchInProgress(true));
      
      const device = micInputDevices.find(d => d.deviceId === deviceId);
      console.log('AudioDeviceSelector: Changing to microphone:', device?.label || deviceId);
      
      dispatch(setSelectedMicDevice(deviceId));
      
      // if (window.CLIENT && typeof window.CLIENT.reinitializeMicrophone === 'function') {
      //   console.log('AudioDeviceSelector: Calling RoomClient reinitializeMicrophone');
      //   setTimeout(() => {
      //     window.CLIENT.reinitializeMicrophone();
      //   }, 100);
      // } else {
      //   console.warn('AudioDeviceSelector: RoomClient not available for microphone reinitialization');
      // }
      
    } catch (error) {
      console.error('AudioDeviceSelector: Failed to change microphone device:', error);
    } finally {
      dispatch(setAudioSwitchInProgress(false));
      console.log('AudioDeviceSelector: Microphone change completed');
    }
  };

  const handleRefreshDevices = async () => {
    if (audioSwitchInProgress) {
      console.log('AudioDeviceSelector: Refresh skipped - operation in progress');
      return;
    }
    
    try {
      console.log('AudioDeviceSelector: Starting device refresh');
      dispatch(setAudioSwitchInProgress(true));
      
      try {
        console.log('AudioDeviceSelector: Requesting audio permission...');
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('AudioDeviceSelector: Audio permission granted');
        audioStream.getTracks().forEach(track => {
          console.log('AudioDeviceSelector: Stopping track:', track.label);
          track.stop();
        });
      } catch (error) {
        console.warn('AudioDeviceSelector: Audio permission not granted:', error);
      }
      
      console.log('AudioDeviceSelector: Enumerating devices...');
      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log('AudioDeviceSelector: All devices found:', devices.map(d => ({
        kind: d.kind,
        label: d.label,
        deviceId: d.deviceId
      })));
      
      const audioOutputs = devices.filter(device => 
        device.kind === 'audiooutput' && device.deviceId
      );
      
      const micInputs = devices.filter(device => 
        device.kind === 'audioinput' && device.deviceId
      );
      
      console.log('AudioDeviceSelector: Filtered audio outputs:', audioOutputs);
      console.log('AudioDeviceSelector: Filtered mic inputs:', micInputs);
      
      dispatch(setAudioOutputDevices(audioOutputs));
      dispatch(setMicInputDevices(micInputs));
      
      if (audioOutputs.length > 0 && !selectedAudioDevice) {
        const defaultOutput = audioOutputs.find(device => device.deviceId === 'default') || audioOutputs[0];
        console.log('AudioDeviceSelector: Auto-selecting output device:', defaultOutput.label);
        dispatch(setSelectedAudioDevice(defaultOutput.deviceId));
      }
      
      if (micInputs.length > 0 && !selectedMicDevice) {
        const defaultInput = micInputs.find(device => device.deviceId === 'default') || micInputs[0];
        console.log('AudioDeviceSelector: Auto-selecting mic device:', defaultInput.label);
        dispatch(setSelectedMicDevice(defaultInput.deviceId));
      }
      
      console.log('AudioDeviceSelector: Device refresh completed');
    } catch (error) {
      console.error('AudioDeviceSelector: Failed to refresh audio devices:', error);
    } finally {
      dispatch(setAudioSwitchInProgress(false));
    }
  };

  const getOutputDeviceIcon = (device: MediaDeviceInfo) => {
    const label = (device.label || "").toLowerCase();
    if (label.includes("headset") || label.includes("headphone")) return <IoHeadset size={16} />;
    return <FiSpeaker size={16} />;
  };

  const getInputDeviceIcon = (device: MediaDeviceInfo) => {
    return <HiMicrophone size={16} />;
  };

  return (
    <div className="audio-device-selector" ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      {showTrigger && (
        <button
          className="audio-trigger"
          onClick={handleToggle}
          disabled={audioSwitchInProgress}
          title="Audio settings"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #5f488a, #7c6ab5)',
            color: 'white',
            border: 'none',
            padding: '8px 10px',
            borderRadius: '10px',
            cursor: 'pointer',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
            minWidth: '40px',
            height: '40px',
            transition: 'all 0.2s ease'
          }}
        >
          <HiVolumeUp size={18} />
          {audioSwitchInProgress && <span style={{
            width: '6px',
            height: '6px',
            background: '#ff4757',
            borderRadius: '50%',
            marginLeft: '4px',
            animation: 'pulse 1.6s infinite'
          }} />}
        </button>
      )}

      {isOpen && (
        <div className="audio-menu" style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: '280px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          color: '#545455',
          fontSize: '13px',
          border: '1px solid #e0e0e0',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #e0e0e0',
            background: 'linear-gradient(135deg, #7c6ab510, transparent)'
          }}>
            <div style={{ fontWeight: 600, color: '#5f488a', fontSize: '14px' }}>Audio Settings</div>
            <button 
              onClick={handleRefreshDevices} 
              disabled={audioSwitchInProgress}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #5f488a, #7c6ab5)',
                color: 'white',
                border: 'none',
                padding: '6px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease',
                width: '28px',
                height: '28px',
                justifyContent: 'center'
              }}
            >
              <HiRefresh size={16} />
            </button>
          </div>

          {/* Tab Selection */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
            <button 
              onClick={() => setActiveTab('output')}
              style={{
                width:'100%',
                height:'100%',
                borderRadius:0,
                boxShadow:'none',
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                color: activeTab === 'output' ? '#5f488a' : '#545455',
                borderBottom: activeTab === 'output' ? '2px solid #5f488a' : '2px solid transparent',
                transition: 'all 0.2s ease'
              }}
            >
              Speaker
            </button>
            <button 
              onClick={() => setActiveTab('input')}
              style={{
                width:'100%',
                height:'100%',
                borderRadius:0,
                boxShadow:'none',
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                color: activeTab === 'input' ? '#5f488a' : '#545455',
                borderBottom: activeTab === 'input' ? '2px solid #5f488a' : '2px solid transparent',
                transition: 'all 0.2s ease'
              }}
            >
              Microphone
            </button>
          </div>

          {/* Output Devices */}
          {activeTab === 'output' && (
            <div style={{ padding: '8px 0' }}>
              <div style={{
                padding: '8px 16px 4px',
                fontSize: '12px',
                color: '#545455',
                fontWeight: 600,
                textTransform: 'uppercase'
              }}>Speakers & Headphones ({audioOutputDevices.length})</div>
              {audioOutputDevices.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#545455', fontSize: '12px' }}>
                  No output devices found
                </div>
              ) : (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {audioOutputDevices.map((device: MediaDeviceInfo) => {
                    const isSelected = selectedAudioDevice === device.deviceId;
                    const displayName = device.deviceId === 'default' 
                      ? 'Default Device' 
                      : device.label || 'Audio Output';
                      
                    return (
                      <div
                        key={device.deviceId}
                        onClick={() => handleAudioOutputChange(device.deviceId)}
                        title={device.label || `Audio Device`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 16px',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease',
                          background: isSelected ? '#5f488a10' : 'transparent'
                        }}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          display: 'grid',
                          placeItems: 'center',
                          background: '#5f488a10',
                          color: '#5f488a',
                          borderRadius: '8px',
                          fontSize: '16px',
                          flexShrink: 0
                        }}>
                          {getOutputDeviceIcon(device)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 500,
                            color: '#222126',
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {displayName}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {isSelected && <HiCheck size={16} color="#5f488a" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Input Devices */}
          {activeTab === 'input' && (
            <div style={{ padding: '8px 0' }}>
              <div style={{
                padding: '8px 16px 4px',
                fontSize: '12px',
                color: '#545455',
                fontWeight: 600,
                textTransform: 'uppercase'
              }}>Microphones ({micInputDevices.length})</div>
              {micInputDevices.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#545455', fontSize: '12px' }}>
                  No microphones found
                </div>
              ) : (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {micInputDevices.map((device: MediaDeviceInfo) => {
                    const isSelected = selectedMicDevice === device.deviceId;
                    const displayName = device.deviceId === 'default' 
                      ? 'Default Microphone' 
                      : device.label || 'Microphone';
                      
                    return (
                      <div
                        key={device.deviceId}
                        onClick={() => handleMicInputChange(device.deviceId)}
                        title={device.label || `Microphone`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 16px',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease',
                          background: isSelected ? '#5f488a10' : 'transparent'
                        }}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          display: 'grid',
                          placeItems: 'center',
                          background: '#5f488a10',
                          color: '#5f488a',
                          borderRadius: '8px',
                          fontSize: '16px',
                          flexShrink: 0
                        }}>
                          {getInputDeviceIcon(device)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 500,
                            color: '#222126',
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {displayName}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {isSelected && <HiCheck size={16} color="#5f488a" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }

        .audio-trigger:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .audio-trigger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
         .audio-menu {
          transform: translateX(-70%) !important;
          margin-bottom: 40px !important;
         }
        }
      `}</style>
    </div>
  );
};

export default AudioDeviceSelector;